/**
 * TTL-aware API response cache backed by localStorage.
 *
 * - Survives page refreshes so repeat visits load instantly from cache.
 * - Falls back to in-memory only when localStorage is unavailable (private mode).
 * - stale-while-revalidate: returns cached data immediately, triggers a
 *   background refresh so next render gets fresh data without blocking UI.
 * - In-flight deduplication: simultaneous requests for the same uncached URL
 *   share one HTTP call instead of firing N separate requests.
 *
 * Usage:
 *   import { cachedGet, invalidateCache } from '../utils/apiCache';
 *
 *   // Basic — 5-min TTL
 *   const data = await cachedGet(axios, url, { ttl: 5 * 60 * 1000 });
 *
 *   // With diagnostics meta
 *   const meta = {};
 *   const data = await cachedGet(axios, url, { ttl: 300_000, swr: 900_000, meta });
 *   // meta.fromCache === true/false, meta.ageMs
 */

const PREFIX = 'pass_cache_v3_';
const DEFAULT_TTL = 5 * 60 * 1000;
const MAX_ENTRIES = 60;

const memoryStore = new Map();
const pendingRequests = new Map(); // in-flight deduplication

function storageGet(key) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return memoryStore.get(key) ?? null;
  }
}

function storageSet(key, value) {
  try {
    const allKeys = Object.keys(localStorage).filter(k => k.startsWith(PREFIX));
    if (allKeys.length >= MAX_ENTRIES) {
      const entries = allKeys
        .map(k => ({ k, t: (() => { try { return JSON.parse(localStorage.getItem(k))?.storedAt ?? 0; } catch { return 0; } })() }))
        .sort((a, b) => a.t - b.t);
      entries.slice(0, Math.ceil(MAX_ENTRIES * 0.2)).forEach(({ k }) => localStorage.removeItem(k));
    }
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    memoryStore.set(key, value);
    if (memoryStore.size > MAX_ENTRIES) {
      memoryStore.delete(memoryStore.keys().next().value);
    }
  }
}

function storageDel(key) {
  try { localStorage.removeItem(PREFIX + key); } catch { /* ignore */ }
  memoryStore.delete(key);
}

/** Build a stable cache key from a URL string (optionally with params object). */
export function cacheKey(url, params = {}) {
  const sorted = Object.keys(params).sort().map(k => `${k}=${params[k]}`).join('&');
  return sorted ? `${url}?${sorted}` : url;
}

/**
 * Fetch with TTL-aware localStorage caching + optional stale-while-revalidate.
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 * @param {string} url  Full URL including query string
 * @param {object} opts
 *   ttl        {number}    ms until entry is expired (default 5 min)
 *   swr        {number}    ms window after ttl where stale data is served + bg refresh runs
 *   params     {object}    merged into cache key only
 *   axiosConfig {object}   extra axios config
 *   onRefresh  {function}  called with fresh data after background revalidation
 *   meta       {object}    populated with { fromCache, ageMs } when provided
 */
export async function cachedGet(axiosInstance, url, {
  ttl = DEFAULT_TTL,
  swr = 0,
  params = {},
  axiosConfig = {},
  onRefresh = null,
  meta = null,
} = {}) {
  const key = cacheKey(url, params);
  const now = Date.now();
  const entry = storageGet(key);

  if (entry) {
    const age = now - entry.storedAt;
    if (age < ttl) {
      if (meta) {
        meta.fromCache = true; meta.ageMs = age;
        meta.serverMs = entry.serverHeaders?.serverMs ?? null;
        meta.dbMs = entry.serverHeaders?.dbMs ?? null;
        meta.cacheStatus = entry.serverHeaders?.cacheStatus ?? null;
      }
      return entry.data;
    }
    if (swr > 0 && age < ttl + swr) {
      if (meta) {
        meta.fromCache = true; meta.ageMs = age;
        meta.serverMs = entry.serverHeaders?.serverMs ?? null;
        meta.dbMs = entry.serverHeaders?.dbMs ?? null;
        meta.cacheStatus = entry.serverHeaders?.cacheStatus ?? null;
      }
      if (onRefresh) fetchAndStore(axiosInstance, url, axiosConfig, key, onRefresh).catch(err => console.error('[apiCache] SWR revalidation failed:', err));
      return entry.data;
    }
  }

  if (meta) { meta.fromCache = false; meta.ageMs = null; }
  const data = await fetchAndStore(axiosInstance, url, axiosConfig, key, onRefresh);
  if (meta) {
    const stored = storageGet(key);
    meta.serverMs = stored?.serverHeaders?.serverMs ?? null;
    meta.dbMs = stored?.serverHeaders?.dbMs ?? null;
    meta.cacheStatus = stored?.serverHeaders?.cacheStatus ?? null;
  }
  return data;
}

async function fetchAndStore(axiosInstance, url, axiosConfig, key, onRefresh) {
  if (pendingRequests.has(key)) return pendingRequests.get(key);

  const promise = axiosInstance.get(url, axiosConfig)
    .then(response => {
      const data = response.data;
      const h = response.headers || {};
      const serverHeaders = {
        serverMs: h['x-query-time-ms'] ? parseInt(h['x-query-time-ms'], 10) : null,
        dbMs: h['x-db-time-ms'] ? parseInt(h['x-db-time-ms'], 10) : null,
        cacheStatus: h['x-cache-status'] || null,
      };
      storageSet(key, { data, storedAt: Date.now(), serverHeaders });
      if (onRefresh) onRefresh(data);
      return data;
    })
    .finally(() => pendingRequests.delete(key));

  pendingRequests.set(key, promise);
  return promise;
}

/** Remove a specific cache entry (call after mutations). */
export function invalidateCache(url, params = {}) {
  storageDel(cacheKey(url, params));
}

/** Remove all PASS cache entries from localStorage. */
export function clearAllCache() {
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(PREFIX))
      .forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
  memoryStore.clear();
}
